import React, { useEffect, useRef, useState } from 'react';
import { CallIcon } from './CallUI';
import './NotificationCenter.css';
import DatePlanner from './DatePlanner';

export default function NotificationCenter({ socket, userId, sessionId, busy, planningPartnerId, onOpenChat, onAcceptCall }) {
  const [notices, setNotices] = useState([]);
  const [call, setCall] = useState(null);
  const [dateInvite, setDateInvite] = useState(null);
  const latest = useRef({ sessionId, busy, planningPartnerId, onOpenChat, onAcceptCall });
  latest.current = { sessionId, busy, planningPartnerId, onOpenChat, onAcceptCall };
  const callRef = useRef(null);
  const dismiss = id => setNotices(items => items.filter(item => item.id !== id));

  useEffect(() => {
    if (!socket) return undefined;
    const seen = new Set();
    const timers = new Set();
    setNotices([]);
    setCall(null);
    setDateInvite(null);
    callRef.current = null;
    const add = notice => {
      if (seen.has(notice.id)) return;
      seen.add(notice.id);
      if (seen.size > 200) seen.delete(seen.values().next().value);
      setNotices(items => [...items.slice(-2), notice]);
      const timer = setTimeout(() => {
        dismiss(notice.id);
        timers.delete(timer);
      }, 12000);
      timers.add(timer);
    };
    const ready = data => {
      if (data.session_id !== latest.current.sessionId || Number(data.partner_id) === Number(userId)) return;
      add({ id: `ready:${data.id}`, title: data.kind === 'continue' ? "הצד השני רוצה להמשיך להכיר" : "הצד השני מוכן",
        text: `${data.partner_username || "הצד השני"} ${data.kind === 'continue' ? "רוצה להמשיך לדבר אחרי החשיפה." : "מוכן להתחיל את השיחה הקולית."}`, icon: 'sparkle' });
    };
    const message = data => {
      if (Number(data.receiver_id) !== Number(userId) || Number(data.sender_id) === Number(userId)) return;
      add({ id: `message:${data.id}`, title: `${data.sender_username || "ההתאמה שלכם"} שלח/ה הודעה`,
        text: (data.text || '').slice(0, 140), partner_id: data.sender_id, partner_username: data.sender_username, icon: 'message' });
    };
    const date = data => {
      if (Number(data.partner_id) === Number(userId)) return;
      // The live planner already receives date_proposal_changed. Opening a
      // second, empty editor here covers the draft the user is working on.
      if (Number(data.partner_id) === Number(latest.current.planningPartnerId)) return;
      setDateInvite(data);
    };
    const incoming = data => {
      if (!['phone', 'video'].includes(data.call_type) || Number(data.from_user_id) === Number(userId)) return;
      if (callRef.current?.from_user_id === data.from_user_id) return;
      if (latest.current.busy || callRef.current) {
        socket.emit('match_call_response', { to_user_id: data.from_user_id, call_type: data.call_type, accepted: false });
        add({ id: `busy:${data.from_user_id}:${Date.now()}`, title: "שיחה בזמן שהייתם עסוקים", text: `${data.from_username || "ההתאמה שלכם"} ניסה/תה להתקשר.`, partner_id: data.from_user_id, partner_username: data.from_username, icon: 'end' });
        return;
      }
      callRef.current = data;
      setCall(data);
    };
    const clearCall = data => {
      if (!data || Number(data.from_user_id ?? data.partner_id) === Number(callRef.current?.from_user_id)) {
        callRef.current = null;
        setCall(null);
      }
    };
    const swipeMatch = data => add({id:`swipe:${data.id}`,title:'יש התאמה חדשה!',text:`גם ${data.partner_username || 'הצד השני'} בעניין. אפשר להתחיל לדבר.`,partner_id:data.partner_id,partner_username:data.partner_username,icon:'sparkle'});
    const handlers = { swipe_match: swipeMatch, partner_ready: ready, new_message: message, date_suggested: date,
      match_call_request: incoming, match_call_cancelled: clearCall, match_call_ended: clearCall,
      disconnect: () => clearCall(), conversation_ended: () => { clearCall(); setNotices([]); setDateInvite(null); } };
    Object.entries(handlers).forEach(([event, handler]) => socket.on(event, handler));
    return () => {
      Object.entries(handlers).forEach(([event, handler]) => socket.off(event, handler));
      timers.forEach(clearTimeout);
    };
  }, [socket, userId]);

  const respond = accepted => {
    const incoming = callRef.current;
    if (!incoming) return;
    callRef.current = null;
    setCall(null);
    socket.emit('match_call_response', { to_user_id: incoming.from_user_id, call_type: incoming.call_type, accepted });
    if (accepted) latest.current.onAcceptCall(incoming);
  };

  return <aside className="notification-center" aria-label="התראות">
    {dateInvite && Number(dateInvite.partner_id) !== Number(planningPartnerId) && <section className="incoming-date-invitation" aria-label="הזמנה לדייט">
      <button className="notice-dismiss" aria-label="סגירת ההזמנה לדייט" onClick={() => setDateInvite(null)}><CallIcon name="close" /></button>
      <p role="status">{dateInvite.partner_username || "ההתאמה שלכם"} הציעו דייט ב־ {dateInvite.location}.</p>
      <DatePlanner key={dateInvite.partner_id} partnerId={dateInvite.partner_id} userId={userId} socket={socket} invitationOnly />
    </section>}
    <div aria-live="polite" aria-relevant="additions">
      {notices.map(notice => <section className="connection-notice" key={notice.id}>
        <span className="notice-symbol"><CallIcon name={notice.icon} /></span>
        <div className="notice-copy"><strong>{notice.title}</strong><p>{notice.text}</p>
          {notice.partner_id && <button onClick={() => { latest.current.onOpenChat(notice); dismiss(notice.id); }}>פתיחת שיחה <span aria-hidden="true">↗</span></button>}
        </div>
        <button className="notice-dismiss" aria-label={`סגירת ${notice.title}`} onClick={() => dismiss(notice.id)}><CallIcon name="close" /></button>
      </section>)}
    </div>
    {call && <section className="connection-notice incoming-call-notice" role="alert" aria-label="שיחה נכנסת">
      <span className="notice-symbol"><CallIcon name={call.call_type === 'video' ? 'video' : 'end'} /></span>
      <div className="notice-copy"><span className="notice-eyebrow">שיחת {call.call_type === 'video' ? 'וידאו' : 'קול'} נכנסת</span>
        <strong>{call.from_username || "ההתאמה שלכם"}</strong><p>שלום קטן, בזמן אמת.</p>
        <div className="notice-actions"><button className="notice-accept" onClick={() => respond(true)}>מענה לשיחה</button><button onClick={() => respond(false)}>דחייה</button></div>
      </div>
    </section>}
  </aside>;
}
